import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StudentAvatar } from "@/components/StudentAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, Plus, CalendarCheck, ClipboardList, Percent, MessageSquarePlus,
  Lock, Unlock, BookOpen, Check, Save, Camera, ScanFace, Pencil,
} from "lucide-react";
import { DiaryAiDialog } from "@/components/DiaryAiDialog";
import { PdfButtons } from "@/components/DiaryPrint";
import { PlannedLessonsCard, MONTHS } from "@/components/PlannedLessons";
import { toast } from "sonner";

const TERMS = [1, 2, 3];
const ATT = { P: { label: "Presente", cls: "bg-green-600" }, F: { label: "Falta", cls: "bg-red-600" }, FJ: { label: "Falta Just.", cls: "bg-amber-500" } };

export default function DiaryWorkspace() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const classId = params.get("class_id");
  const subjectId = params.get("subject_id");
  const className = params.get("cn") || "";
  const subjectName = params.get("sn") || "";
  const [term, setTerm] = useState(1);
  const [diaryStatus, setDiaryStatus] = useState(null);
  const canReopen = ["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user.role);

  const refreshStatus = useCallback(() => {
    if (!classId || !subjectId) return;
    api.get("/diary-status", { params: { class_id: classId, subject_id: subjectId, term_order: term } })
      .then((r) => setDiaryStatus(r.data)).catch(() => {});
  }, [classId, subjectId, term]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  if (!classId || !subjectId) return <div className="py-16 text-center text-slate-500">Diário inválido. <Button variant="link" onClick={() => navigate("/meu-diario")}>Voltar</Button></div>;

  const closed = diaryStatus?.status === "FECHADO";

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => navigate("/meu-diario")} className="mb-3 -ml-2 text-slate-500"><ArrowLeft className="mr-1 h-4 w-4" /> Meu Diário</Button>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{subjectName || "Disciplina"}</h1>
          <p className="text-sm text-slate-500">Turma {className} · Diário de Classe</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PdfButtons path="/diary/pdf" params={{ class_id: classId, subject_id: subjectId, term_order: term }}
            filename={`diario_${className}_${subjectName}_${term}tri.pdf`} testid="diary-print" />
          {closed ? <Badge className="bg-slate-700">Diário FECHADO</Badge> : <Badge className="bg-green-600">Diário ABERTO</Badge>}
          <Select value={String(term)} onValueChange={(v) => setTerm(Number(v))}>
            <SelectTrigger className="w-36" data-testid="diary-term-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="lessons">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          <TabsTrigger value="lessons" data-testid="diary-tab-lessons" className="gap-1.5 data-[state=active]:bg-white"><CalendarCheck className="h-4 w-4" /> Aulas & Chamada</TabsTrigger>
          <TabsTrigger value="grades" data-testid="diary-tab-grades" className="gap-1.5 data-[state=active]:bg-white"><ClipboardList className="h-4 w-4" /> Avaliações & Notas</TabsTrigger>
          <TabsTrigger value="frequency" data-testid="diary-tab-frequency" className="gap-1.5 data-[state=active]:bg-white"><Percent className="h-4 w-4" /> Frequência</TabsTrigger>
          <TabsTrigger value="notes" data-testid="diary-tab-notes" className="gap-1.5 data-[state=active]:bg-white"><MessageSquarePlus className="h-4 w-4" /> Observações</TabsTrigger>
          <TabsTrigger value="close" data-testid="diary-tab-close" className="gap-1.5 data-[state=active]:bg-white"><Lock className="h-4 w-4" /> Fechamento</TabsTrigger>
        </TabsList>

        <TabsContent value="lessons" className="mt-4"><LessonsTab classId={classId} subjectId={subjectId} term={term} closed={closed} /></TabsContent>
        <TabsContent value="grades" className="mt-4"><GradesTab classId={classId} subjectId={subjectId} term={term} closed={closed} /></TabsContent>
        <TabsContent value="frequency" className="mt-4"><FrequencyTab classId={classId} subjectId={subjectId} term={term} /></TabsContent>
        <TabsContent value="notes" className="mt-4"><NotesTab classId={classId} subjectId={subjectId} term={term} /></TabsContent>
        <TabsContent value="close" className="mt-4">
          <CloseTab classId={classId} subjectId={subjectId} term={term} status={diaryStatus} closed={closed} canReopen={canReopen} onChange={refreshStatus} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Aulas & Chamada ---------------- */
function LessonsTab({ classId, subjectId, term, closed }) {
  const [lessons, setLessons] = useState(null);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", num_times: 1, content: "", objective: "", activity: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [callLesson, setCallLesson] = useState(null);
  const [att, setAtt] = useState({});
  const [attSrc, setAttSrc] = useState({});
  const [aiOpen, setAiOpen] = useState(false);
  const [savingCall, setSavingCall] = useState(false);
  const [reload, setReload] = useState(0);
  const [edit, setEdit] = useState(null);
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { id, class_id, subject_id, academic_year_id, term_order, date, num_times, content, objective, activity, notes } = edit;
      await api.put(`/lessons/${id}`, { class_id, subject_id, academic_year_id, term_order, date, num_times: Number(num_times) || 1, content, objective, activity, notes });
      toast.success("Conteúdo atualizado"); setEdit(null); fetchLessons();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const addOn = (month) => {
    const y = lessons?.[0]?.date?.slice(0, 4) || String(new Date().getFullYear());
    const today = new Date().toISOString().slice(0, 10);
    const date = Number(today.slice(5, 7)) === month ? today : `${y}-${String(month).padStart(2, "0")}-01`;
    setForm((f) => ({ ...f, date })); setOpen(true);
  };
  const fetchLessons = useCallback(() => {
    api.get("/lessons", { params: { class_id: classId, subject_id: subjectId, term_order: term } }).then((r) => { setLessons(r.data); setReload((k) => k + 1); });
  }, [classId, subjectId, term]);
  useEffect(() => { fetchLessons(); api.get(`/classes/${classId}/students`).then((r) => setStudents(r.data)); }, [fetchLessons, classId]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/lessons", { class_id: classId, subject_id: subjectId, term_order: term, ...form, num_times: Number(form.num_times) });
      toast.success("Aula registrada"); setOpen(false); setForm({ date: "", num_times: 1, content: "", objective: "", activity: "", notes: "" }); fetchLessons();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openCall = (lesson) => {
    const map = {};
    const src = {};
    students.forEach((s) => { map[s.id] = "F"; });
    (lesson.attendance || []).forEach((a) => { map[a.student_id] = a.status; src[a.student_id] = a; });
    setAtt(map); setAttSrc(src); setCallLesson(lesson);
  };
  const cycle = (sid) => setAtt((m) => ({ ...m, [sid]: m[sid] === "P" ? "F" : m[sid] === "F" ? "FJ" : "P" }));
  const saveCall = async () => {
    setSavingCall(true);
    try {
      await api.post(`/lessons/${callLesson.id}/attendance`, { attendance: Object.entries(att).map(([student_id, status]) => ({ student_id, status })) });
      toast.success("Chamada salva"); setCallLesson(null); fetchLessons();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingCall(false); }
  };

  if (lessons === null) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-700" />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => setAiOpen(true)} disabled={closed} data-testid="diary-ai-read-btn"><Camera className="mr-1.5 h-4 w-4" /> Ler foto do diário</Button>
        <Button onClick={() => setOpen(true)} disabled={closed} data-testid="lesson-add-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Registrar Aula</Button>
      </div>
      <PlannedLessonsCard classId={classId} subjectId={subjectId} term={term} closed={closed} reloadKey={reload} />
      {lessons.length === 0 ? <Card className="py-12 text-center text-sm text-slate-400">Nenhuma aula registrada neste trimestre.</Card> : (
        <div className="grid gap-4 xl:grid-cols-3">
          {[...new Set(lessons.map((l) => Number(l.date.slice(5, 7))))].map((m) => (
            <Card key={m} className="overflow-hidden border-slate-300 p-0" data-testid={`content-month-${m}`}>
              <div className="grid grid-cols-[52px_1fr] border-b border-slate-300 bg-slate-50 text-xs font-bold uppercase text-slate-700">
                <span className="border-r border-slate-300 px-2 py-2 text-center">Dia</span>
                <span className="flex items-center justify-between px-3 py-2">Conteúdos/Mês: {MONTHS[m]}
                  <button type="button" onClick={() => addOn(m)} disabled={closed} className="rounded p-0.5 text-blue-700 hover:bg-blue-50 disabled:opacity-40" data-testid={`content-month-add-${m}`}><Plus className="h-4 w-4" /></button>
                </span>
              </div>
              {lessons.filter((l) => Number(l.date.slice(5, 7)) === m).sort((a, b) => a.date.localeCompare(b.date)).map((l) => {
                const present = (l.attendance || []).filter((a) => a.status === "P").length;
                const done = (l.attendance || []).some((a) => a.source === "MANUAL");
                const kiosk = (l.attendance || []).filter((a) => a.source === "QUIOSQUE" && a.status === "P").length;
                return (
                  <div key={l.id} className="grid grid-cols-[52px_1fr] border-b border-slate-200 last:border-b-0" data-testid={`lesson-card-${l.id}`}>
                    <span className="border-r border-slate-200 px-2 py-2 text-center font-display text-base font-bold text-blue-700">{l.date.slice(8, 10)}</span>
                    <div className="min-w-0 px-3 py-2">
                      <button type="button" disabled={closed} onClick={() => setEdit({ ...l })} title={closed ? "" : "Clique para corrigir o conteúdo"} className="group flex w-full items-start gap-1.5 rounded text-left text-sm font-medium text-slate-800 hover:text-blue-800 disabled:cursor-default disabled:hover:text-slate-800" data-testid={`lesson-edit-${l.id}`}>
                        <span className="min-w-0 flex-1 break-words">{l.content || <span className="italic text-slate-400">Sem conteúdo — clique para preencher</span>}</span>
                        {!closed && <Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-blue-700" />}
                      </button>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                        <span>{l.num_times} tempo(s)</span>
                        {(l.attendance || []).length > 0 && <span data-testid={`lesson-presence-${l.id}`}>{present}/{l.attendance.length} presentes{kiosk ? ` · ${kiosk} pelo quiosque` : ""}</span>}
                        {done && <Badge className="bg-green-100 text-green-700"><Check className="mr-0.5 h-3 w-3" /> Chamada feita</Badge>}
                        <Button variant="outline" size="sm" onClick={() => openCall(l)} disabled={closed} className="ml-auto h-7 px-2 text-xs" data-testid={`lesson-call-${l.id}`}><CalendarCheck className="mr-1 h-3.5 w-3.5" /> Chamada</Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Card>
          ))}
        </div>
      )}

      {/* Registrar aula */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Registrar Aula</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data *</Label><Input type="date" data-testid="lesson-date-input" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
              <div><Label>Nº de tempos</Label><Input type="number" min="1" value={form.num_times} onChange={(e) => setForm((f) => ({ ...f, num_times: e.target.value }))} /></div>
            </div>
            <div><Label>Conteúdo ministrado</Label><Textarea rows={2} data-testid="lesson-content-input" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} /></div>
            <div><Label>Objetivo/Habilidade</Label><Input value={form.objective} onChange={(e) => setForm((f) => ({ ...f, objective: e.target.value }))} /></div>
            <div><Label>Atividade realizada</Label><Input value={form.activity} onChange={(e) => setForm((f) => ({ ...f, activity: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={create} disabled={saving || !form.date} data-testid="lesson-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Corrigir aula — {edit && new Date(edit.date + "T12:00:00").toLocaleDateString("pt-BR")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input type="date" value={edit.date} onChange={(e) => setEdit((f) => ({ ...f, date: e.target.value }))} data-testid="lesson-edit-date" /></div>
                <div><Label>Nº de tempos</Label><Input type="number" min="1" value={edit.num_times} onChange={(e) => setEdit((f) => ({ ...f, num_times: e.target.value }))} data-testid="lesson-edit-times" /></div>
              </div>
              <div><Label>Conteúdo ministrado</Label><Textarea rows={3} autoFocus value={edit.content || ""} onChange={(e) => setEdit((f) => ({ ...f, content: e.target.value }))} data-testid="lesson-edit-content" /></div>
              <div><Label>Objetivo/Habilidade</Label><Input value={edit.objective || ""} onChange={(e) => setEdit((f) => ({ ...f, objective: e.target.value }))} /></div>
              <div><Label>Atividade realizada</Label><Input value={edit.activity || ""} onChange={(e) => setEdit((f) => ({ ...f, activity: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={saveEdit} disabled={saving || !edit?.date} data-testid="lesson-edit-save" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DiaryAiDialog open={aiOpen} setOpen={setAiOpen} classId={classId} subjectId={subjectId} term={term} students={students} onDone={fetchLessons} />

      {/* Chamada */}
      <Dialog open={!!callLesson} onOpenChange={(o) => !o && setCallLesson(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Chamada — {callLesson && new Date(callLesson.date).toLocaleDateString("pt-BR")}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Todos começam com falta; quem passa no quiosque ganha presença automática (<ScanFace className="inline h-3 w-3" />). 1º tempo: tolerância de 15 min; nos demais, chegar depois do início conta falta naquele tempo. Toque no estudante para alternar: Presente → Falta → Falta Justificada.</p>
          <div className="space-y-2">
            {students.map((s) => (
              <button key={s.id} data-testid={`attendance-${s.id}`} onClick={() => cycle(s.id)} className="flex w-full items-center justify-between rounded-xl border border-slate-200 p-2.5 text-left hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <StudentAvatar student={s} className="h-8 w-8" fallbackClassName="bg-blue-100 text-xs text-blue-700" />
                  <span className="text-sm font-medium text-slate-700">{s.full_name}</span>
                  {attSrc[s.id]?.source === "QUIOSQUE" && <span className="flex items-center gap-0.5 text-[11px] text-green-700" data-testid={`attendance-kiosk-${s.id}`}><ScanFace className="h-3.5 w-3.5" />{attSrc[s.id].arrival}{attSrc[s.id].missed ? ` · ${attSrc[s.id].missed} tempo(s) de falta` : att[s.id] === "F" ? " · chegou após os tempos" : ""}</span>}
                </div>
                <span className={`min-w-[56px] rounded-lg px-2 py-1 text-center text-xs font-bold text-white ${att[s.id] === "P" && attSrc[s.id]?.missed ? "bg-lime-600" : ATT[att[s.id] || "F"].cls}`}>{att[s.id] || "F"}{att[s.id] === "P" && attSrc[s.id]?.missed ? " parcial" : ""}</span>
              </button>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCallLesson(null)}>Cancelar</Button><Button onClick={saveCall} disabled={savingCall} data-testid="attendance-save-button" className="bg-blue-700 hover:bg-blue-800">{savingCall ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar Chamada"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Avaliações & Notas ---------------- */
function GradesTab({ classId, subjectId, term, closed }) {
  const [data, setData] = useState(null);
  const [grid, setGrid] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [savingGrades, setSavingGrades] = useState(false);
  const fetchData = useCallback(() => {
    api.get("/gradebook", { params: { class_id: classId, subject_id: subjectId, term_order: term } }).then((r) => {
      setData(r.data);
      const g = {};
      r.data.rows.forEach((row) => { r.data.assessments.forEach((a) => { g[`${a.id}:${row.student_id}`] = row.grades[a.id] ?? ""; }); });
      setGrid(g);
    });
  }, [classId, subjectId, term]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const createAssessment = async () => {
    setSaving(true);
    try {
      await api.post("/assessments", { class_id: classId, subject_id: subjectId, term_order: term, date: form.date || null, description: form.description || null });
      toast.success("Recuperação adicionada"); setOpen(false); setForm({ date: "", description: "" }); fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveGrades = async () => {
    setSavingGrades(true);
    try {
      for (const a of data.assessments) {
        const grades = data.rows.map((row) => ({ student_id: row.student_id, value: grid[`${a.id}:${row.student_id}`] === "" ? null : Number(grid[`${a.id}:${row.student_id}`]) }));
        await api.post(`/assessments/${a.id}/grades`, { grades });
      }
      toast.success("Notas salvas"); fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingGrades(false); }
  };

  if (data === null) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-700" />;
  const hasRecovery = data.assessments.some((a) => a.is_recovery);

  return (
    <div>
      <div className="mb-4 flex justify-end gap-2">
        {!hasRecovery && false && <Button onClick={() => setOpen(true)} disabled={closed} variant="outline" data-testid="assessment-add-button"><Plus className="mr-1.5 h-4 w-4" /> Adicionar Recuperação</Button>}
        {data.assessments.length > 0 && <Button onClick={saveGrades} disabled={closed || savingGrades} data-testid="grades-save-button" className="bg-blue-700 hover:bg-blue-800">{savingGrades ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Salvar Notas</>}</Button>}
      </div>
      {data.assessments.length === 0 ? <Card className="py-12 text-center text-sm text-slate-400">Nenhuma avaliação. Crie uma para lançar notas.</Card> : (
        <Card className="overflow-x-auto border-slate-200" data-testid="gradebook-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="sticky left-0 bg-white">Estudante</TableHead>
              {data.assessments.map((a) => <TableHead key={a.id} className="text-center">{a.name}<div className="text-[10px] font-normal text-slate-400">0 a {a.max_value}{a.is_recovery ? " · só se nota < 5,0" : ""}</div></TableHead>)}
              <TableHead className="text-center">Nota do trimestre</TableHead>
              <TableHead className="text-center">Situação</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={row.student_id}>
                  <TableCell className="sticky left-0 bg-white font-medium text-slate-800">{row.full_name}</TableCell>
                  {data.assessments.map((a) => (
                    <TableCell key={a.id} className="text-center">
                      <Input type="number" min={0} max={a.max_value} step="0.1" data-testid={`grade-${a.id}-${row.student_id}`} value={grid[`${a.id}:${row.student_id}`] ?? ""}
                        disabled={closed || (a.is_recovery && (row.base == null || row.base >= 5) && !(grid[`${a.id}:${row.student_id}`] ?? ""))}
                        onChange={(e) => setGrid((g) => ({ ...g, [`${a.id}:${row.student_id}`]: e.target.value }))}
                        className="mx-auto h-9 w-20 text-center" />
                    </TableCell>
                  ))}
                  <TableCell className="text-center font-bold text-slate-900">{row.final ?? "—"}</TableCell>
                  <TableCell className="text-center">
                    {row.situation === "SATISFATORIO" && <Badge className="bg-green-100 text-green-700">Satisfatório</Badge>}
                    {row.situation === "ABAIXO" && <Badge className="bg-red-100 text-red-700">Abaixo</Badge>}
                    {!row.situation && <span className="text-xs text-slate-300">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <p className="mt-2 text-xs text-slate-400">Nota do trimestre = Trabalho (0 a 2) + Teste (0 a 3) + Prova (0 a 5). Média para aprovação: {data.passing_grade}. Recuperação (0 a 10) preserva a nota original.</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Adicionar Recuperação</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <p className="text-sm text-slate-500">A recuperação vale de 0 a 10 e segue a regra de recuperação da escola.</p>
            <div><Label>Data</Label><Input type="date" data-testid="assessment-date-input" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><Label>Observação</Label><Input data-testid="assessment-description-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={createAssessment} disabled={saving} data-testid="assessment-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Adicionar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Frequência ---------------- */
function FrequencyTab({ classId, subjectId, term }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/frequency", { params: { class_id: classId, subject_id: subjectId, term_order: term } }).then((r) => setData(r.data)); }, [classId, subjectId, term]);
  if (data === null) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-700" />;
  return (
    <Card className="overflow-x-auto border-slate-200">
      <div className="border-b border-slate-100 p-4 text-sm font-semibold text-slate-700">Aulas ministradas no trimestre: <span className="text-blue-700">{data.aulas_ministradas}</span></div>
      <Table>
        <TableHeader><TableRow><TableHead>Estudante</TableHead><TableHead className="text-center">Presenças</TableHead><TableHead className="text-center">Faltas</TableHead><TableHead className="text-center">F. Just.</TableHead><TableHead className="text-center">Frequência</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow key={r.student_id}>
              <TableCell className="font-medium text-slate-800">{r.full_name}</TableCell>
              <TableCell className="text-center">{r.presencas}</TableCell>
              <TableCell className="text-center">{r.faltas}</TableCell>
              <TableCell className="text-center">{r.faltas_justificadas}</TableCell>
              <TableCell className="text-center"><Badge className={(r.percentual ?? 0) >= 75 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>{r.percentual ?? "—"}%</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ---------------- Observações pedagógicas ---------------- */
function NotesTab({ classId, subjectId, term }) {
  const [notes, setNotes] = useState(null);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", text: "" });
  const [saving, setSaving] = useState(false);

  const fetchNotes = useCallback(() => { api.get("/pedagogical-notes", { params: { class_id: classId } }).then((r) => setNotes(r.data)); }, [classId]);
  useEffect(() => { fetchNotes(); api.get(`/classes/${classId}/students`).then((r) => setStudents(r.data)); }, [fetchNotes, classId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/pedagogical-notes", { ...form, class_id: classId, subject_id: subjectId, term_order: term });
      toast.success("Observação registrada"); setOpen(false); setForm({ student_id: "", text: "" }); fetchNotes();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (notes === null) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-700" />;
  const sName = (id) => students.find((s) => s.id === id)?.full_name || "Estudante";
  return (
    <div>
      <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">A observação pedagógica NÃO é uma ocorrência disciplinar e não desclassifica o estudante da Excelência Acadêmica.</div>
      <div className="mb-4 flex justify-end"><Button onClick={() => setOpen(true)} data-testid="note-add-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Nova Observação</Button></div>
      {notes.length === 0 ? <Card className="py-12 text-center text-sm text-slate-400">Nenhuma observação registrada.</Card> : (
        <div className="space-y-3">
          {notes.map((n) => (
            <Card key={n.id} data-testid={`note-${n.id}`} className="border-slate-200 p-4">
              <div className="flex items-center justify-between"><span className="font-semibold text-slate-800">{sName(n.student_id)}</span><span className="text-xs text-slate-400">{n.date ? new Date(n.date).toLocaleDateString("pt-BR") : ""}</span></div>
              <p className="mt-1.5 text-sm text-slate-600">{n.text}</p>
              {n.author && <p className="mt-1 text-xs text-slate-400">Por {n.author}</p>}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nova Observação Pedagógica</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Estudante *</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm((f) => ({ ...f, student_id: v }))}>
                <SelectTrigger data-testid="note-student-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Observação *</Label><Textarea rows={4} data-testid="note-text-input" value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={saving || !form.student_id || !form.text} data-testid="note-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Fechamento ---------------- */
function CloseTab({ classId, subjectId, term, status, closed, canReopen, onChange }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    if (action === "reopen" && !reason) { toast.error("Informe o motivo da reabertura"); return; }
    setBusy(true);
    try {
      await api.post(`/diary-status/${action}`, { class_id: classId, subject_id: subjectId, term_order: term, reason });
      toast.success(action === "close" ? "Diário fechado" : "Diário reaberto"); setReason(""); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="max-w-xl border-slate-200 p-6">
      <div className="flex items-center gap-3">
        <div className={`grid h-12 w-12 place-items-center rounded-xl ${closed ? "bg-slate-200" : "bg-green-100"}`}>{closed ? <Lock className="h-6 w-6 text-slate-600" /> : <Unlock className="h-6 w-6 text-green-700" />}</div>
        <div><h3 className="font-display text-lg font-bold text-slate-900">Diário {closed ? "FECHADO" : "ABERTO"} — {term}º Trimestre</h3>
          {closed && status?.closed_by && <p className="text-xs text-slate-400">Fechado por {status.closed_by} em {status.closed_at ? new Date(status.closed_at).toLocaleString("pt-BR") : ""}</p>}
        </div>
      </div>

      {!closed ? (
        <div className="mt-5">
          <p className="mb-3 text-sm text-slate-500">Revise aulas, conteúdos, frequência e notas antes de fechar. Após o fechamento, lançamentos ficam bloqueados.</p>
          <Button onClick={() => act("close")} disabled={busy} data-testid="diary-close-button" className="bg-slate-800 hover:bg-slate-900"><Lock className="mr-1.5 h-4 w-4" /> Fechar Diário do Trimestre</Button>
        </div>
      ) : (
        <div className="mt-5">
          {canReopen ? (
            <>
              <Label>Motivo da reabertura *</Label>
              <Textarea rows={2} data-testid="diary-reopen-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" />
              <Button onClick={() => act("reopen")} disabled={busy} data-testid="diary-reopen-button" className="mt-3 bg-orange-600 hover:bg-orange-700"><Unlock className="mr-1.5 h-4 w-4" /> Reabrir Diário</Button>
            </>
          ) : <p className="text-sm text-slate-500">Somente a Coordenação/Direção pode reabrir este diário.</p>}
          {(status?.reopen_history || []).length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Histórico de reaberturas</p>
              {status.reopen_history.map((h, i) => <div key={i} className="mt-1.5 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{h.user} · {new Date(h.at).toLocaleString("pt-BR")} — {h.reason}</div>)}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
